// /eventos/:id — Detalle de evento + selección de entrada en línea
// La selección se hace aquí para ahorrar un paso en el checkout (más conversión).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Heart, Star, Minus, Plus, Loader2 } from 'lucide-react';
import { COLORS, fmtDayPretty, fmtTime } from './designSystem';
import { EventsService } from './EventsService';
import { ColorBlockImage, useSafeBack } from './components';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useSafeBack('/eventos');
  const [event, setEvent] = useState(null);
  const [liked, setLiked] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    (async () => setEvent(await EventsService.getEvent(id)))();
    try { setLiked((JSON.parse(localStorage.getItem('fav_events') || '[]')).includes(String(id))); } catch (e) { /* noop */ }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setTicketsLoading(true);
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/ticket-packages`);
        const data = await res.json();
        const evTickets = data[String(id)] || [];
        setTickets(evTickets);
        const first = evTickets.find((t) => !t.soldOut) || evTickets[0] || null;
        if (first) setSelectedTicketId(first.id);
      } catch (e) {
        setTickets([]);
      } finally {
        setTicketsLoading(false);
      }
    })();
  }, [id]);

  const toggleLike = () => {
    let arr = []; try { arr = JSON.parse(localStorage.getItem('fav_events') || '[]'); } catch (e) { /* noop */ }
    arr = liked ? arr.filter((x) => String(x) !== String(id)) : [...new Set([...arr, String(id)])];
    localStorage.setItem('fav_events', JSON.stringify(arr));
    setLiked(!liked);
  };

  const selectedTicket = useMemo(() => tickets.find((t) => t.id === selectedTicketId) || null, [tickets, selectedTicketId]);
  const total = (Number(selectedTicket?.price) || 0) * quantity;
  const pointsEarned = Math.floor(total * 10);

  if (!event) return (<div style={{ backgroundColor: COLORS.bg, minHeight: '100vh', color: COLORS.text }}><p className="p-5">Cargando…</p></div>);

  const providerConectado = event.provider === 'stripe';
  const canBuy = providerConectado && !!selectedTicket && !selectedTicket.soldOut;

  const handleBuy = () => {
    if (!canBuy) return;
    navigate(`/eventos/${id}/comprar?ticket_id=${selectedTicket.id}&qty=${quantity}`);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000' }}>
      <div className="mx-auto max-w-[430px] relative overflow-x-hidden" style={{ backgroundColor: COLORS.bg, minHeight: '100vh', paddingBottom: 112 }}>
        <div className="relative">
          <ColorBlockImage src={event.image || event.imagen_url} venueId={event.venue_id} className="w-full h-[320px]" iconSize={80} />
          <button onClick={goBack} className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center rounded-full backdrop-blur" style={{ backgroundColor: 'rgba(10,10,12,0.6)' }} data-testid="event-back"><ChevronLeft size={22} color="#fff" /></button>
          <button onClick={toggleLike} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full backdrop-blur" style={{ backgroundColor: 'rgba(10,10,12,0.6)' }} data-testid="event-like"><Heart size={20} color="#fff" fill={liked ? COLORS.primary : 'transparent'} /></button>
        </div>

        <div className="px-5 pt-5">
          <h1 className="text-white text-2xl font-extrabold mb-2 leading-tight">{event.name}</h1>
          <p className="text-sm mb-3" style={{ color: COLORS.coral }}>
            <button onClick={() => navigate(`/venues/${event.venue_id}`)} className="underline-offset-2 hover:underline">{event.venue}</button>
            {event.fecha_inicio && (<> · {fmtTime(event.fecha_inicio)} · <span className="capitalize">{fmtDayPretty(event.fecha_inicio)}</span></>)}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap mb-5">
            <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ backgroundColor: COLORS.surfaceAlt, color: COLORS.text }}>+{event.edad_minima || 18}</span>
            {(event.estilos || []).map((s) => (<span key={s} className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ backgroundColor: COLORS.surfaceAlt, color: COLORS.text }}>{s}</span>))}
            {event.ultimas_entradas && <span className="px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ backgroundColor: COLORS.primaryDark, color: COLORS.coral }}>Últimas entradas</span>}
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ border: `1px solid ${COLORS.primaryDark}`, color: COLORS.coral }}><Star size={11} fill={COLORS.coral} color={COLORS.coral} />+{event.puntos || 0} pts</span>
          </div>
          <p className="text-sm leading-relaxed mb-6" style={{ color: COLORS.textMuted }}>{event.description || 'Más info próximamente.'}</p>

          {providerConectado && (
            <section className="mb-5" data-testid="event-tickets-section">
              <h3 className="text-white font-bold text-base mb-3">Entradas disponibles</h3>
              {ticketsLoading ? (
                <div className="flex items-center gap-2 text-sm py-4" style={{ color: COLORS.textMuted }}>
                  <Loader2 size={16} className="animate-spin" /> Cargando entradas…
                </div>
              ) : tickets.length === 0 ? (
                <div className="p-4 rounded-2xl text-sm" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted }}>
                  No hay entradas publicadas todavía.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {tickets.map((t) => {
                    const sel = selectedTicketId === t.id;
                    const sold = t.soldOut;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        disabled={sold}
                        onClick={() => !sold && setSelectedTicketId(t.id)}
                        data-testid={`event-ticket-option-${t.id}`}
                        className="w-full flex items-center justify-between p-3.5 rounded-2xl text-left transition-colors"
                        style={{
                          backgroundColor: COLORS.surface,
                          border: `1.5px solid ${sel && !sold ? COLORS.primary : COLORS.border}`,
                          opacity: sold ? 0.5 : 1,
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ border: `2px solid ${sel && !sold ? COLORS.primary : 'rgba(255,255,255,0.3)'}` }}>
                            {sel && !sold && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS.primary }} />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-bold text-sm leading-tight">{t.label}</p>
                            {t.note && !sold && <p className="text-[11px] mt-0.5" style={{ color: COLORS.coral }}>{t.note}</p>}
                          </div>
                        </div>
                        {sold ? (
                          <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: COLORS.textMuted }}>Agotadas</span>
                        ) : (
                          <span className="font-extrabold text-white whitespace-nowrap tabular-nums" translate="no">{`${Number(t.price).toFixed(0)}€`}</span>
                        )}
                      </button>
                    );
                  })}

                  {/* Cantidad */}
                  <div className="flex items-center justify-between p-3.5 rounded-2xl mt-2" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                    <span className="text-white font-semibold text-sm">Cantidad</span>
                    <div className="flex items-center gap-4">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: COLORS.surfaceAlt }} data-testid="event-qty-minus"><Minus size={14} color="#fff" /></button>
                      <span className="text-white font-bold text-base w-5 text-center tabular-nums" translate="no">{quantity}</span>
                      <button onClick={() => setQuantity(quantity + 1)} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: COLORS.surfaceAlt }} data-testid="event-qty-plus"><Plus size={14} color="#fff" /></button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {!providerConectado && (
            <div className="p-4 rounded-2xl mb-4 text-sm" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted }}>
              Este evento se gestiona vía <strong>{event.provider}</strong> y aún no está conectado. En cuanto lo esté, podrás comprar sin salir de Clubbers.
            </div>
          )}
        </div>

        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] px-5 py-4" style={{ backgroundColor: COLORS.bg, borderTop: `1px solid ${COLORS.border}` }}>
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: COLORS.textMuted }}>Total</p>
              <p className="text-white font-extrabold text-xl tabular-nums" translate="no">
                {selectedTicket ? `${total.toFixed(2)}€` : (event.price > 0 ? `Desde ${Number(event.price).toFixed(0)}€` : 'Gratis')}
              </p>
              {selectedTicket && pointsEarned > 0 && (
                <p className="text-[11px] mt-0.5" style={{ color: COLORS.coral }}>+{pointsEarned} pts</p>
              )}
            </div>
            <button
              onClick={handleBuy}
              disabled={!canBuy}
              className="flex-1 py-3.5 rounded-full font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: COLORS.primary }}
              data-testid="event-buy-btn"
            >
              {providerConectado ? 'Comprar' : 'Próximamente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
